import './styles.scss';
import React, { useState } from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Menu from 'antd/lib/menu';
import message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import copy from 'copy-to-clipboard';
import {
    CaretDownOutlined, CaretUpFilled, EyeInvisibleFilled, EyeOutlined, FileSearchOutlined, LockFilled, UnlockOutlined,
} from '@ant-design/icons';

import CVATTooltip from 'components/common/cvat-tooltip';
import CategoryRow from 'components/annotation-page/standard-workspace/objects-side-bar/category-row';
import SidebarToolbar from 'components/annotation-page/standard-workspace/objects-side-bar/sidebar-toolbar';
import { t } from 'utils/i18n';
import { useReferencesContext } from './references-context';
import {
    coverageLabel, Detection, filterByLabel, groupByLabel, resolveModel, summarize,
} from './reference-model';
import { useDetectionCrops } from './detection-crop';
import DetectionRow from './detection-row';
import { useWindowsPaths } from './use-windows-paths';

type Scope = 'photo' | 'all';

export default function ReferencesPanel(): JSX.Element | null {
    const references = useReferencesContext();
    const [scope, setScope] = useState<Scope>('photo');
    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState<string[]>([]);
    const [allCollapsed, setAllCollapsed] = useState(false);
    const photoItems = references?.photoDetections ?? [];
    const allItems = references?.detections ?? [];
    const items = scope === 'photo' ? photoItems : allItems;
    const shown = filterByLabel(items, query);
    const crops = useDetectionCrops(shown);
    const windowsPaths = useWindowsPaths(shown.map((item) => item.value).filter(Boolean));
    if (!references) return null;

    const {
        finding, error, activeClientID, backendAPI, photoCount, find, cancelFind, select, openPicker, photoNumber,
        remove, setFlags,
    } = references;
    const { total, withModel, missing } = summarize(items);
    const groups = groupByLabel(shown);
    const allHidden = shown.length > 0 && shown.every((item) => item.hidden);
    const allLocked = shown.length > 0 && shown.every((item) => item.lock);

    const copyPath = (path: string): void => {
        if (copy(path)) message.success({ content: t('Path copied'), className: 'cvat-references-path-copied' });
        else message.error(t('Could not copy the path'));
    };
    const confirmDelete = (targets: Detection[], title: string): void => {
        Modal.confirm({
            title,
            content: t('The detections are removed from the photos and the change is saved.'),
            okText: t('Delete'),
            okButtonProps: { danger: true },
            onOk: () => remove(targets),
        });
    };
    // "collapsed" lists the exceptions to the all-collapsed or all-expanded default
    const isCollapsed = (label: string): boolean => (
        allCollapsed ? !collapsed.includes(label) : collapsed.includes(label)
    );
    const toggle = (label: string): void => {
        setCollapsed((prev) => (prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]));
    };
    const collapseAll = (value: boolean): void => {
        setAllCollapsed(value);
        setCollapsed([]);
    };

    const row = (item: Detection): JSX.Element => (
        <DetectionRow
            key={item.clientID}
            detection={item}
            model={resolveModel(item, backendAPI)}
            crop={crops[item.clientID]}
            selected={item.clientID === activeClientID}
            windowsPath={windowsPaths[item.value]}
            photoLabel={scope === 'all' ? t('Photo {n}', { n: photoNumber(item.frame) }) : undefined}
            onSelect={() => select(item)}
            onPick={() => openPicker(item)}
            onCopyPath={copyPath}
            onDelete={() => confirmDelete([item], t('Delete this {label}?', { label: item.labelName }))}
        />
    );

    let list: React.ReactNode;
    if (!items.length) {
        list = (
            <span className='cvat-references-empty'>
                {scope === 'photo' ? t('No detections on this photo') : t('No detections in this reference')}
            </span>
        );
    } else if (!shown.length) {
        list = <span className='cvat-references-empty'>{t('No category matches the search')}</span>;
    } else {
        list = groups.map((group) => {
            const open = !isCollapsed(group.label);
            const groupMenu = (
                <Menu>
                    <Menu.Item
                        key='delete'
                        danger
                        onClick={() => confirmDelete(
                            group.items,
                            t('Delete {n} {label}?', { n: group.items.length, label: group.label }),
                        )}
                    >
                        {t('Delete all {n}', { n: group.items.length })}
                    </Menu.Item>
                </Menu>
            );
            return (
                <div key={group.label} className='cvat-references-group' data-label={group.label}>
                    <CategoryRow
                        name={group.label}
                        count={group.items.length}
                        color={group.items[0].labelColor}
                        open={open}
                        hidden={group.items.every((item) => item.hidden)}
                        locked={group.items.every((item) => item.lock)}
                        onToggle={() => toggle(group.label)}
                        onHide={(hidden) => setFlags(group.items, { hidden })}
                        onLock={(lock) => setFlags(group.items, { lock })}
                        menu={groupMenu}
                    />
                    {open && group.items.map(row)}
                </div>
            );
        });
    }

    return (
        <div className='cvat-references-panel'>
            <div className='cvat-references-header'>
                <div className='cvat-references-summary'>{coverageLabel(withModel, total)}</div>
                <div className='cvat-references-find'>
                    <Button
                        type='primary'
                        icon={<FileSearchOutlined />}
                        disabled={!!finding || missing.length === 0}
                        onClick={() => find(missing)}
                    >
                        {finding ? t('Finding models… {done} of {total}', finding) : t('Find models')}
                    </Button>
                    {finding ? <Button onClick={cancelFind}>{t('Cancel')}</Button> : (
                        <Select
                            className='cvat-references-scope'
                            value={scope}
                            onChange={setScope}
                            dropdownMatchSelectWidth={false}
                            virtual={false}
                            aria-label={t('Photos to work on')}
                        >
                            <Select.Option value='photo'>{t('on this photo')}</Select.Option>
                            <Select.Option value='all'>{t('on all {n}', { n: photoCount })}</Select.Option>
                        </Select>
                    )}
                </div>
                <SidebarToolbar search={query} onSearch={setQuery}>
                    <CVATTooltip title={t('Switch lock property for all')}>
                        {allLocked ? (
                            <LockFilled aria-label={t('Unlock all')} onClick={() => setFlags(shown, { lock: false })} />
                        ) : (
                            <UnlockOutlined aria-label={t('Lock all')} onClick={() => setFlags(shown, { lock: true })} />
                        )}
                    </CVATTooltip>
                    <CVATTooltip title={t('Switch hidden property for all')}>
                        {allHidden ? (
                            <EyeInvisibleFilled aria-label={t('Show all')} onClick={() => setFlags(shown, { hidden: false })} />
                        ) : (
                            <EyeOutlined aria-label={t('Hide all')} onClick={() => setFlags(shown, { hidden: true })} />
                        )}
                    </CVATTooltip>
                    <CVATTooltip title={t('Expand/collapse all')}>
                        {allCollapsed ? (
                            <CaretDownOutlined aria-label={t('Expand all')} onClick={() => collapseAll(false)} />
                        ) : (
                            <CaretUpFilled aria-label={t('Collapse all')} onClick={() => collapseAll(true)} />
                        )}
                    </CVATTooltip>
                </SidebarToolbar>
            </div>
            {error ? <Alert type='error' message={error} /> : null}
            <div className='cvat-references-list'>{list}</div>
        </div>
    );
}
